import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WebCustomer } from 'src/Model/WebCustomer';
import { DesignService } from '../design/design.service';
import { brandUrl, isRouteRoot } from './brand-context';

/** Where the bare brand URL lands when the brand has no (usable) TemplateData.DefaultPage. */
export const FALLBACK_DEFAULT_PAGE = 'menu';

/**
 * The landing page for `/` (brand host) and `/<flavor>` — also the old
 * `/home`, which never had a page behind it. Sends the visitor to
 * `TemplateData.DefaultPage` ('menu', 'reservation/service-booking', …),
 * falling back to the menu, keeping the query string and fragment.
 *
 * Waits for the brand config the way DesignRouteGuard does (DesignService
 * caps the wait), so a slow API still lands somewhere. The replacement
 * navigation is issued by the router, which treats a guard's UrlTree as a
 * redirect — the bare URL never enters the history.
 */
@Injectable({ providedIn: 'root' })
export class DefaultPageGuard implements CanActivate {
  constructor(private design: DesignService, private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<UrlTree> {
    return this.design.webCustomer$.pipe(
      map((res) => this.target(defaultPageOf(res), state.url))
    );
  }

  private target(page: string, currentUrl: string): UrlTree {
    const current = this.router.parseUrl(currentUrl);
    const tree = this.router.parseUrl('/' + brandUrl(page));
    tree.queryParams = { ...tree.queryParams, ...current.queryParams };
    tree.fragment = current.fragment ?? tree.fragment;
    return tree;
  }
}

/**
 * `TemplateData.DefaultPage` when it names one of the app's pages (a route
 * root, optionally with child segments and a query string), else 'menu'.
 */
export function defaultPageOf(res: WebCustomer | null | undefined): string {
  const raw = (res?.TemplateData?.DefaultPage || '').trim().replace(/^\/+/, '');
  if (!raw) return FALLBACK_DEFAULT_PAGE;
  const root = raw.split(/[/?#]/)[0];
  return isRouteRoot(root) && root !== 'home' ? raw : FALLBACK_DEFAULT_PAGE;
}
